import { CopyIterable } from "../async/CopyIterable"
import { ReduceAsync } from "../async/ReduceAsync"
import { StringToUInt8 } from "../bytes/StringToUInt8"
import { Uint8Crc32 } from "../hashing/UInt8Crc32"
import { FromUnixTime } from "../time/FromUnixTime"
import { UnixTime } from "../time/UnixTime"
import type { AsyncSequence } from "../types/AsyncSequence"
import type { Time } from "../types/Units"

function dos_time(timestamp: Time): number {
  const date = FromUnixTime(timestamp)
  const seconds = Math.floor(date.getUTCSeconds() / 2)
  const minutes = date.getUTCMinutes()
  const hours = date.getUTCHours()
  return (hours << 11) | (minutes << 5) | seconds
}

function dos_date(timestamp: Time): number {
  const date = FromUnixTime(timestamp)
  const day = date.getUTCDate()
  const month = date.getUTCMonth() + 1
  const year = date.getUTCFullYear()
  return ((year - 1980) << 9) | (month << 5) | day
}

export async function* ZipCreate(
  files: AsyncSequence<{
    name: string
    chunks: AsyncSequence<Uint8Array>
    modified?: Time
  }>
): AsyncGenerator<Uint8Array> {
  const now = UnixTime()
  const centralDirectory: Uint8Array[] = []
  let offset = 0

  for await (const file of files) {
    const time = dos_time(file.modified ?? now)
    const date = dos_date(file.modified ?? now)

    const [copy1, copy2, copy3] = CopyIterable(file.chunks, 3)

    const hash = Uint8Crc32(copy1)
    const sizePromise = ReduceAsync(
      (acc, chunk) => acc + chunk.length,
      copy2,
      0
    )

    const filenameBytes = StringToUInt8(file.name)
    const localHeaderOffset = offset

    // Wait for size and crc32
    const [size, crc32] = await Promise.all([sizePromise, hash])

    const header = new Uint8Array(30 + filenameBytes.length)
    const view = new DataView(header.buffer)

    view.setUint32(0, 0x04034b50, true) // LOCAL_FILE_HEADER_SIGNATURE
    view.setUint16(4, 20, true) // version needed to extract
    view.setUint16(6, 0x0800, true) // general purpose bit flag (bit 11 for UTF-8, no data descriptor)
    view.setUint16(8, 0, true) // compression method (0 = store)
    view.setUint16(10, time, true)
    view.setUint16(12, date, true)
    view.setUint32(14, crc32, true) // crc32
    view.setUint32(18, size, true) // compressed size
    view.setUint32(22, size, true) // uncompressed size
    view.setUint16(26, filenameBytes.length, true) // filename length
    view.setUint16(28, 0, true) // extra field length
    header.set(filenameBytes, 30)

    yield header
    offset += header.length

    for await (const chunk of copy3) {
      yield chunk
      offset += chunk.length
    }

    // Central Directory Entry
    const central = new Uint8Array(46 + filenameBytes.length)
    const centralView = new DataView(central.buffer)

    centralView.setUint32(0, 0x02014b50, true) // CENTRAL_DIRECTORY_HEADER_SIGNATURE
    centralView.setUint32(4, 0x2d032d00, true) // version made by (UNIX 4.5) + version needed (4.5)
    centralView.setUint16(8, 0x0800, true) // general purpose bit flag (same as local header)
    centralView.setUint16(10, 0, true) // compression method
    centralView.setUint16(12, time, true)
    centralView.setUint16(14, date, true)
    centralView.setUint32(16, crc32, true)
    centralView.setUint32(20, size, true)
    centralView.setUint32(24, size, true)
    centralView.setUint16(28, filenameBytes.length, true)
    centralView.setUint16(30, 0, true) // extra field length
    centralView.setUint16(32, 0, true) // file comment length
    centralView.setUint16(34, 0, true) // disk number start
    centralView.setUint16(36, 0, true) // internal file attributes
    centralView.setUint16(38, 0o100000, true) // external file attributes - file mode (regular file)
    centralView.setUint16(40, 0, true) // external file attributes high bytes
    centralView.setUint32(42, localHeaderOffset, true) // relative offset of local header
    central.set(filenameBytes, 46)

    centralDirectory.push(central)
  }

  const centralDirStart = offset
  for (const entry of centralDirectory) {
    yield entry
    offset += entry.length
  }

  const eocd = new Uint8Array(22)
  const eocdView = new DataView(eocd.buffer)
  eocdView.setUint32(0, 0x06054b50, true) // END_OF_CENTRAL_DIR_SIGNATURE
  eocdView.setUint16(4, 0, true) // number of this disk
  eocdView.setUint16(6, 0, true) // disk where central directory starts
  eocdView.setUint16(8, centralDirectory.length, true) // number of central dir records on this disk
  eocdView.setUint16(10, centralDirectory.length, true) // total number of central dir records
  eocdView.setUint32(12, offset - centralDirStart, true) // size of central directory
  eocdView.setUint32(16, centralDirStart, true) // offset of start of central directory
  eocdView.setUint16(20, 0, true) // comment length

  yield eocd
}
